import { useMemo, useCallback, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, Briefcase, User } from 'lucide-react';
import type { Project, GridColumn, Person } from '../types';
import { useT } from '../i18n';
import { getProjectStageGroup, type ProjectStageGroup } from '../utils/stages';
import {
  getPersonInvolvement, getProjectCollaborators,
  type PersonProjectInvolvement,
} from '../utils/allocation';
import { getProjectMilestones, getMilestoneProgress } from '../utils/milestoneProgress';
import { fmtShortDate } from '../utils/date';
import { DropdownSelect } from './DropdownSelect';
import { roleDisplay } from '../proposals/roles-projet/projectRoles';

// Resource-planning view for Gérance: pick a person, see every project they
// touch — one row per project (sponsor and/or collaborator, never listed
// twice), grouped into active / deferred-or-backlog / closed, so parked and
// finished projects stay visible without diluting the "current load" stats
// or crowding out the active table.

const GROUP_ORDER: ProjectStageGroup[] = ['active', 'deferredBacklog', 'closed'];
const GROUP_LABEL_KEY: Record<ProjectStageGroup, string> = {
  active: 'resources.groupActive',
  deferredBacklog: 'resources.groupDeferredBacklog',
  closed: 'resources.groupClosed',
};

function emptyGroups<T>(): Record<ProjectStageGroup, T[]> {
  return { active: [], deferredBacklog: [], closed: [] };
}

function goToProject(id: number) {
  window.location.hash = `project?id=${id}`;
}

export function GeranceResourcesView({ projects, pipelineColumns, people, projectDeadlines, personId, onPersonChange, selfView = false }: {
  projects: Project[];
  pipelineColumns: GridColumn[];
  people: Person[];
  projectDeadlines: Map<number, string>;
  personId: number | '';
  onPersonChange: (id: number | '') => void;
  // Non-admin mode: the view is locked to the current user — hide the person
  // picker and the per-project "other collaborators" breakdown, so they only
  // ever see their own involvement.
  selfView?: boolean;
}) {
  const { t } = useT();
  const [expandedProjects, setExpandedProjects] = useState<Set<number>>(new Set());

  const toggleProject = useCallback((projectId: number) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
      return next;
    });
  }, []);

  const projectsById = useMemo(() => new Map(projects.map(p => [p.id, p])), [projects]);
  const groupOf = useCallback(
    (projectId: number): ProjectStageGroup => {
      const p = projectsById.get(projectId);
      return p ? getProjectStageGroup(p.status_id, pipelineColumns) : 'active';
    },
    [projectsById, pipelineColumns]
  );

  const personOptions = useMemo(
    () => people.slice().sort((a, b) => a.name.localeCompare(b.name)).map(p => ({ value: p.id, label: p.name })),
    [people]
  );

  const person = personId ? people.find(p => p.id === personId) || null : null;

  const involvementRows = useMemo(
    () => person ? getPersonInvolvement(person.name, projects) : [],
    [person, projects]
  );

  const involvementGroups = useMemo(() => {
    const groups = emptyGroups<PersonProjectInvolvement>();
    for (const row of involvementRows) groups[groupOf(row.projectId)].push(row);
    return groups;
  }, [involvementRows, groupOf]);

  const totalHoursPerWeek = useMemo(
    () => involvementGroups.active.reduce((sum, r) => sum + r.hoursPerWeek, 0),
    [involvementGroups]
  );
  const totalPct = useMemo(
    () => involvementGroups.active.reduce((sum, r) => sum + r.pct, 0),
    [involvementGroups]
  );
  const milestoneProgressByProject = useMemo(() => {
    const map = new Map<number, { currentIndex: number; total: number } | null>();
    for (const p of projects) map.set(p.id, getMilestoneProgress(getProjectMilestones(p)));
    return map;
  }, [projects]);

  const renderGroup = (group: ProjectStageGroup) => {
    const rows = involvementGroups[group];
    if (rows.length === 0 || !person) return null;
    const muted = group !== 'active';
    return (
      <div key={group} className="mb-48">
        <div className="res-group-label">{t(GROUP_LABEL_KEY[group])}</div>
        <table className={`table res-alloc-table${muted ? ' res-group-muted' : ''}`}>
          <thead>
            <tr>
              <th>{t('projectDetail.alloc.colProject')}</th>
              <th style={{ width: 120 }} className="res-type-th">
                <div className="res-type-header">
                  <span><Briefcase size={11} className="icon-inline" /> {t('resources.typeSponsor')}</span>
                  <span><User size={11} className="icon-inline" /> {t('resources.typeCollaborator')}</span>
                </div>
              </th>
              <th style={{ width: 180 }}>{t('projectDetail.alloc.colRole')}</th>
              <th style={{ width: 90 }}>{t('resources.colAllocation')}</th>
              <th style={{ width: 100 }}>{t('resources.colHoursPerWeek')}</th>
              <th style={{ width: 130 }}>{t('resources.colStage')}</th>
              <th style={{ width: 100 }}>{t('resources.colMilestone')}</th>
              <th style={{ width: 110 }}>{t('resources.colDeadline')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const progress = milestoneProgressByProject.get(row.projectId);
              const isExpanded = !selfView && expandedProjects.has(row.projectId);
              const project = projectsById.get(row.projectId);
              const others = isExpanded && project ? getProjectCollaborators(project, person.name) : [];
              return (
                <Fragment key={`${row.projectId}-${i}`}>
                  <tr className="echeance-row" onClick={() => goToProject(row.projectId)}>
                    <td className="font-medium text-ink-1" title={row.projectName}>
                      {!selfView && (
                        <button
                          type="button"
                          className="res-row-expand-btn"
                          title={t('resources.otherCollaborators')}
                          onClick={(e) => { e.stopPropagation(); toggleProject(row.projectId); }}
                        >
                          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </button>
                      )}
                      {row.projectName}
                    </td>
                    <td className="text-ink-3">
                      <span className="res-type-icons">
                        {row.isSponsor && <span title={t('resources.typeSponsor')}><Briefcase size={13} /></span>}
                        {row.isCollaborator && <span title={t('resources.typeCollaborator')}><User size={13} /></span>}
                      </span>
                    </td>
                    {/* [Proposition: roles-projet] role + précision, role explanation on hover */}
                    {(() => { const r = roleDisplay(t, row.projectRole, row.role); return <td className="text-ink-3" title={r.title ?? (r.text || undefined)}>{r.text || '—'}</td>; })()}
                    <td className="text-ink-1">{row.isCollaborator ? `${row.pct}%` : '—'}</td>
                    <td className="text-ink-1">{row.isCollaborator ? `${row.hoursPerWeek.toFixed(1)} h` : '—'}</td>
                    <td className="text-ink-3">{project?.status_label || '—'}</td>
                    <td className="text-ink-3">{progress ? t('resources.milestoneN', { n: progress.currentIndex + 1, total: progress.total }) : '—'}</td>
                    <td className="text-ink-3">{fmtShortDate(projectDeadlines.get(row.projectId)) || '—'}</td>
                  </tr>
                  {isExpanded && others.length === 0 && (
                    <tr className="res-subrow">
                      <td colSpan={8}>
                        <div className="res-subrow-empty">{t('resources.noOtherCollaborators')}</div>
                      </td>
                    </tr>
                  )}
                  {isExpanded && others.map((o, j) => (
                    <tr key={j} className="res-subrow">
                      <td className="res-subrow-name" title={o.name}>{o.name}</td>
                      <td className="text-ink-3">—</td>
                      {(() => { const r = roleDisplay(t, o.projectRole, o.role); return <td className="text-ink-3" title={r.title ?? (r.text || undefined)}>{r.text || '—'}</td>; })()}
                      <td className="text-ink-1">{o.pct}%</td>
                      <td className="text-ink-1">{o.hoursPerWeek.toFixed(1)} h</td>
                      <td className="text-ink-3">—</td>
                      <td className="text-ink-3">—</td>
                      <td className="text-ink-3">—</td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {!selfView && (
        <div className="toolbar mb-16">
          <DropdownSelect
            value={personId}
            options={personOptions}
            placeholder={t('resources.pickPerson')}
            onChange={(v) => onPersonChange(v === '' ? '' : Number(v))}
            triggerStyle={{ width: 280 }}
          />
        </div>
      )}

      {!person ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('resources.emptyTitle')}</div>
          <div className="empty-state-desc">{t('resources.emptyDesc')}</div>
        </div>
      ) : (
        <>
          <div className="stat-card res-summary-card mb-24">
            <div className="stat-value">{totalHoursPerWeek.toFixed(1)} h <span className="res-total-pct">({totalPct}%)</span></div>
            <div className="stat-label">{t('resources.totalHoursPerWeek')}</div>
          </div>

          {involvementRows.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-title">{t('resources.noProjectsTitle')}</div>
              <div className="empty-state-desc">{t('resources.noProjectsDesc', { name: person.name })}</div>
            </div>
          ) : (
            GROUP_ORDER.map(renderGroup)
          )}
        </>
      )}
    </>
  );
}
